import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Select from 'react-select';
import { roundDownTotal } from './Consumables/consumableHelpers';
import { ConsumableInvoiceDocument } from '../components/BillInvoicePreview';
import { downloadInvoicePDF } from '../utils/invoicePdf';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Patient {
  id: number;
  patient_name: string;
  age: string;
  sex: string;
  treating_hospital?: string;
  treating_doctor?: string;
  treating_details?: { doctor: string; hospital: string; department: string }[];
  case_start_date: string;
  address: string;
  patient_clients: { client_name: string; contact_number_1: string; email: string }[];
}

interface ActiveBatch {
  batch_id: number;
  consumable_id: number;
  consumable_name: string;
  gst_percent: number | null;
  batch_no: string | null;
  hsn_sac: string | null;
  mrp: number | null;
  selling_price: number | null;
  dealer_price: number | null;
  current_quantity: number;
  expiry_date: string | null;
  vendor_name: string | null;
}

interface ConsumableLineItem {
  batch_id: number | null;
  batch_display: string;
  hsn_sac: string;
  dealer_price: number | '';
  mrp: number | '';
  disc_price: number;
  discount_rate: number;
  quantity: number | '';
  gst_percent: number;
  cgst_amount: number;
  sgst_amount: number;
  subtotal: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtAmt = (n: number) =>
  n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_OPTIONS = ['Pending', 'Collected', 'Processed'];

const emptyItem = (): ConsumableLineItem => ({
  batch_id: null,
  batch_display: '',
  hsn_sac: '',
  dealer_price: '',
  mrp: '',
  disc_price: 0,
  discount_rate: 0,
  quantity: '',
  gst_percent: 0,
  cgst_amount: 0,
  sgst_amount: 0,
  subtotal: 0,
});

const computeDownstream = (item: ConsumableLineItem): ConsumableLineItem => {
  const qty = Number(item.quantity) || 0;
  const gst_percent = item.gst_percent || 0;
  const taxable_value = item.disc_price * qty;
  const cgst = taxable_value * ((gst_percent / 2) / 100);
  const sgst = taxable_value * ((gst_percent / 2) / 100);
  const subtotal = taxable_value + cgst + sgst;
  return { ...item, cgst_amount: cgst, sgst_amount: sgst, subtotal };
};

const batchDisplay = (b: ActiveBatch) =>
  `${b.consumable_name}${b.batch_no ? ` — Batch #${b.batch_no}` : ''}`;

// ─── Main Component ───────────────────────────────────────────────────────────

const EditConsumableBill: React.FC = () => {
  const { billId } = useParams<{ billId: string }>();
  const navigate = useNavigate();

  const [patient, setPatient] = useState<Patient | null>(null);
  const [activeBatches, setActiveBatches] = useState<ActiveBatch[]>([]);
  const [billNo, setBillNo] = useState<number | ''>('');
  // Printed consumable series, fixed at creation — never re-issued on edit.
  const [invoiceNo, setInvoiceNo] = useState('');
  const [billDate, setBillDate] = useState('');
  const [expectedPaymentDate, setExpectedPaymentDate] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [status, setStatus] = useState('Pending');
  const [remarks, setRemarks] = useState('');
  const [lineItems, setLineItems] = useState<ConsumableLineItem[]>([emptyItem()]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const invoiceRef = useRef<HTMLDivElement>(null);

  const token = localStorage.getItem('accessToken');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchInitial = useCallback(async () => {
    if (!billId) return;
    try {
      const [billRes, invoiceItemsRes, batchRes] = await Promise.all([
        axios.get(`${API_URL}/api/billing/bills/${billId}`, { headers }),
        axios.get(`${API_URL}/api/consumables/invoice-items/${billId}`, { headers }),
        axios.get(`${API_URL}/api/consumables/active-batches`, { headers }),
      ]);

      const bill = billRes.data;
      const invoiceItems: { id: number; batch_id: number; quantity: number }[] = invoiceItemsRes.data;
      const batches: ActiveBatch[] = batchRes.data;

      const patientRes = await axios.get(`${API_URL}/api/patients/${bill.patient_id}`, { headers });
      setPatient(patientRes.data);
      setActiveBatches(batches);

      setBillNo(bill.bill_no);
      setInvoiceNo(bill.invoice_no || '');
      setBillDate(bill.bill_date);
      setExpectedPaymentDate(bill.expected_payment_date || '');
      setPeriodStart(bill.invoice_period_start);
      setPeriodEnd(bill.invoice_period_end);
      setStatus(bill.status);
      setRemarks(bill.remarks || '');

      const reconstructed: ConsumableLineItem[] = bill.line_items.map(
        (li: any, idx: number) => {
          const invoiceItem = invoiceItems[idx];
          const batch = invoiceItem
            ? batches.find((b) => b.batch_id === invoiceItem.batch_id) ?? null
            : null;

          const disc_price = li.rate ?? 0;
          const mrp = batch?.mrp ?? 0;
          const discount_rate = mrp > 0 ? ((mrp - disc_price) / mrp) * 100 : 0;
          const hsnMatch = (li.details || '').match(/HSN\/SAC:\s*(.+)/);

          return computeDownstream({
            batch_id: invoiceItem?.batch_id ?? null,
            batch_display: batch ? batchDisplay(batch) : (li.particulars || ''),
            hsn_sac: hsnMatch ? hsnMatch[1].trim() : '',
            dealer_price: batch?.dealer_price ?? '',
            mrp: mrp || '',
            disc_price,
            discount_rate,
            quantity: li.shifts_count ?? '',
            gst_percent: li.gst_percent ?? 0,
            cgst_amount: 0,
            sgst_amount: 0,
            subtotal: 0,
          });
        }
      );

      setLineItems(reconstructed.length > 0 ? reconstructed : [emptyItem()]);
    } catch {
      toast.error('Failed to load invoice data');
    } finally {
      setLoading(false);
    }
  }, [billId]);

  useEffect(() => { fetchInitial(); }, [fetchInitial]);

  // ── line item helpers ───────────────────────────────────────────────────

  const selectBatch = (idx: number, batch: ActiveBatch) => {
    setLineItems((prev) => prev.map((item, i) => {
      if (i !== idx) return item;
      const mrp = batch.mrp ?? 0;
      // Pre-fill discounted price with the batch's selling price (fallback MRP) and
      // back-fill the discount % to match; the user can still override either.
      const disc_price = batch.selling_price ?? mrp;
      const discount_rate = mrp > 0 ? ((mrp - disc_price) / mrp) * 100 : 0;
      return computeDownstream({
        ...item,
        batch_id: batch.batch_id,
        batch_display: batchDisplay(batch),
        hsn_sac: batch.hsn_sac ?? '',
        dealer_price: batch.dealer_price ?? '',
        mrp,
        disc_price,
        discount_rate,
        gst_percent: batch.gst_percent ?? 0,
      });
    }));
  };

  const updateItem = (idx: number, field: keyof ConsumableLineItem, value: number | string) => {
    setLineItems((prev) => prev.map((item, i) => {
      if (i !== idx) return item;
      const next = { ...item, [field]: value };
      const mrp = Number(next.mrp) || 0;

      if (field === 'discount_rate') {
        next.disc_price = mrp - (mrp * ((next.discount_rate || 0) / 100));
      } else if (field === 'disc_price') {
        next.discount_rate = mrp > 0 ? ((mrp - next.disc_price) / mrp) * 100 : 0;
      } else if (field === 'mrp') {
        next.disc_price = mrp - (mrp * ((next.discount_rate || 0) / 100));
      } else if (field === 'cgst_amount' || field === 'sgst_amount') {
        const taxable = next.disc_price * (Number(next.quantity) || 0);
        next.subtotal = taxable + next.cgst_amount + next.sgst_amount;
        return next;
      }

      return computeDownstream(next);
    }));
  };

  const removeItem = (idx: number) =>
    setLineItems((prev) => prev.filter((_, i) => i !== idx));

  // ── save ────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!billDate) { toast.error('Bill date is required'); return; }
    if (lineItems.length === 0) { toast.error('Add at least one item'); return; }
    if (lineItems.some((i) => !i.batch_id)) { toast.error('Select a batch for each row'); return; }
    if (lineItems.some((i) => !i.quantity || Number(i.quantity) <= 0)) {
      toast.error('Enter quantity for each row');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        bill_date: billDate,
        expected_payment_date: expectedPaymentDate || null,
        invoice_period_start: periodStart,
        invoice_period_end: periodEnd,
        status,
        remarks: remarks || null,
        items: lineItems.map((item) => ({
          batch_id: item.batch_id!,
          quantity: Number(item.quantity),
          particulars: item.batch_display || 'Consumable',
          rate: item.disc_price,
          details: item.hsn_sac ? `HSN/SAC: ${item.hsn_sac}` : '',
          gst_percent: item.gst_percent,
          total_amount: item.subtotal,
        })),
      };

      await axios.put(`${API_URL}/api/consumables/invoice/${billId}`, payload, { headers });
      toast.success('Invoice updated successfully');
      navigate(`/billing/patient/${patient!.id}`);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to update invoice');
    } finally {
      setSaving(false);
    }
  };

  // ── PDF download ─────────────────────────────────────────────────────────

  const handleDownloadPDF = async () => {
    if (!invoiceRef.current) { toast.error('Preview not ready'); return; }
    setDownloadingPDF(true);
    try {
      await new Promise((r) => setTimeout(r, 300));
      await downloadInvoicePDF(
        invoiceRef.current,
        `Consumable_Invoice_${patient?.patient_name || 'Patient'}_${(invoiceNo || String(billNo)).replace(/\//g, '-')}.pdf`,
      );
    } catch {
      toast.error('Failed to generate PDF');
    } finally {
      setDownloadingPDF(false);
    }
  };

  // ── computed ─────────────────────────────────────────────────────────────

  const totalExGst = lineItems.reduce((sum, i) => sum + i.disc_price * (Number(i.quantity) || 0), 0);
  const totalCgst  = lineItems.reduce((s, i) => s + i.cgst_amount, 0);
  const totalSgst  = lineItems.reduce((s, i) => s + i.sgst_amount, 0);
  // Payable is floored to whole rupees — see roundDownTotal(). This is the
  // figure shown here, printed on the invoice and stored on the bill.
  const itemsTotal = lineItems.reduce((s, i) => s + i.subtotal, 0);
  const grandTotal = roundDownTotal(itemsTotal);
  // The paise dropped by the floor, shown to the biller so the summary
  // visibly adds up instead of the total silently disagreeing with the parts.
  const roundOff = Math.round((itemsTotal - grandTotal) * 100) / 100;

  // ── styles ────────────────────────────────────────────────────────────────

  const s: Record<string, React.CSSProperties> = {
    page:           { padding: '2rem', backgroundColor: '#f8fafc', minHeight: '100vh' },
    topBar:         { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' },
    backBtn:        { background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '0.9rem' },
    title:          { margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#0f172a' },
    subtitle:       { margin: '3px 0 0', fontSize: '0.85rem', color: '#64748b' },
    actions:        { display: 'flex', gap: '10px' },
    card:           { backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '1.5rem', marginBottom: '1.5rem' },
    sectionTitle:   { margin: '0 0 1rem', fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', borderBottom: '1px solid #f1f5f9', paddingBottom: '8px' },
    patientInfo:    { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem' },
    infoLabel:      { fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' as const, letterSpacing: '0.04em' },
    infoValue:      { fontSize: '0.88rem', color: '#334155', marginTop: '2px' },
    fieldLabel:     { fontSize: '0.75rem', fontWeight: 600, color: '#64748b', marginBottom: '4px', display: 'block', textTransform: 'uppercase' as const, letterSpacing: '0.04em' },
    input:          { width: '100%', padding: '0.55rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.88rem', boxSizing: 'border-box' as const },
    select:         { width: '100%', padding: '0.55rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.88rem', boxSizing: 'border-box' as const, backgroundColor: 'white' },
    table:          { width: '100%', borderCollapse: 'collapse' as const, tableLayout: 'fixed' as const, marginBottom: '0.5rem' },
    th:             { padding: '0.55rem 0.5rem', fontSize: '0.7rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' as const, letterSpacing: '0.04em', backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0', textAlign: 'left' as const },
    td:             { padding: '0.4rem 0.4rem', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' as const },
    cellInput:      { width: '100%', padding: '0.38rem 0.45rem', borderRadius: '5px', border: '1px solid #e2e8f0', fontSize: '0.8rem', boxSizing: 'border-box' as const },
    summaryBox:     { backgroundColor: '#f8fafc', borderRadius: '8px', padding: '1rem', border: '1px solid #e2e8f0', minWidth: '300px' },
    summaryLine:    { display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '0.88rem', color: '#64748b' },
    grandTotalLine: { display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', borderTop: '2px solid #e2e8f0', marginTop: '4px' },
    deleteBtn:      { padding: '0.28rem 0.45rem', border: 'none', borderRadius: '4px', cursor: 'pointer', backgroundColor: '#fee2e2', color: '#991b1b', fontSize: '0.78rem' },
    textarea:       { width: '100%', padding: '0.55rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.88rem', resize: 'vertical' as const, minHeight: '80px', boxSizing: 'border-box' as const },
  };

  const btn = (variant: string): React.CSSProperties => ({
    padding: '0.55rem 1.2rem', borderRadius: '8px', cursor: 'pointer',
    fontSize: '0.88rem', fontWeight: 600,
    backgroundColor: variant === 'primary' ? '#2563eb' : 'white',
    color: variant === 'primary' ? 'white' : '#334155',
    border: variant === 'primary' ? 'none' : '1px solid #cbd5e1',
    opacity: saving ? 0.7 : 1,
  });

  if (loading) return <div style={s.page}><div style={{ textAlign: 'center', color: '#94a3b8', paddingTop: '4rem' }}>Loading…</div></div>;

  const client = patient?.patient_clients?.[0];

  const batchOptions = activeBatches.map((b) => ({
    value: b.batch_id,
    label: batchDisplay(b),
    batch: b,
  }));

  return (
    <div style={s.page}>
      {/* Top bar */}
      <div style={s.topBar}>
        <div>
          <button style={s.backBtn} onClick={() => navigate(patient ? `/billing/patient/${patient.id}` : '/billing')}>
            ← Back
          </button>
          <h2 style={s.title}>Edit Consumable Invoice</h2>
          {patient && <p style={s.subtitle}>{patient.patient_name} — CID: LHHS{patient.id} — {invoiceNo || `Bill #${billNo}`}</p>}
        </div>
        <div style={s.actions}>
          <button style={btn('outline')} onClick={() => setShowPreview(true)}>Preview Invoice</button>
          {status !== 'Deleted' && (
            <button style={btn('primary')} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          )}
        </div>
      </div>

      {/* Client details */}
      {patient && (
        <div style={s.card}>
          <p style={s.sectionTitle}>Client Details</p>
          <div style={s.patientInfo}>
            {[
              { label: 'Client',   value: client?.client_name || '—' },
              { label: 'Contact',  value: client?.contact_number_1 || '—' },
              { label: 'CID',      value: `LHHS${patient.id}` },
              { label: 'Sex',      value: patient.sex || '—' },
              { label: 'Patient',  value: patient.patient_name },
              { label: 'Age',      value: patient.age || '—' },
              { label: 'Hospital', value: patient.treating_details?.[0]?.hospital || patient.treating_hospital || '—' },
              { label: 'Doctor',   value: patient.treating_details?.[0]?.doctor || patient.treating_doctor || '—' },
            ].map(({ label, value }) => (
              <div key={label}>
                <div style={s.infoLabel}>{label}</div>
                <div style={{ ...s.infoValue, fontWeight: label === 'Client' ? 700 : undefined }}>{value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bill details */}
      <div style={s.card}>
        <p style={s.sectionTitle}>Bill Details</p>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <label style={s.fieldLabel}>{invoiceNo ? 'Invoice Number' : 'Bill Number'}</label>
            <input type="text" style={{ ...s.input, backgroundColor: '#f8fafc', color: '#94a3b8' }}
              value={invoiceNo || billNo} readOnly />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <label style={s.fieldLabel}>Bill Date</label>
            <input type="date" style={s.input} value={billDate} onChange={(e) => setBillDate(e.target.value)} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <label style={s.fieldLabel}>Expected Payment Date</label>
            <input type="date" style={s.input} value={expectedPaymentDate} onChange={(e) => setExpectedPaymentDate(e.target.value)} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <label style={s.fieldLabel}>Period Start</label>
            <input type="date" style={s.input} value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <label style={s.fieldLabel}>Period End</label>
            <input type="date" style={s.input} value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <label style={s.fieldLabel}>Status</label>
            <select style={s.select} value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUS_OPTIONS.map((o) => <option key={o}>{o}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Line items */}
      <div style={s.card}>
        <p style={s.sectionTitle}>Consumable Items</p>
        <div style={{ overflowX: 'auto' }}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={{ ...s.th, width: '3%',  textAlign: 'center' }}>#</th>
                <th style={{ ...s.th, width: '19%' }}>Item / Batch</th>
                <th style={{ ...s.th, width: '7%'  }}>HSN/SAC</th>
                <th style={{ ...s.th, width: '7%',  textAlign: 'right' }}>Dealer Price ₹</th>
                <th style={{ ...s.th, width: '7%',  textAlign: 'right' }}>MRP ₹</th>
                <th style={{ ...s.th, width: '6%',  textAlign: 'right' }}>Disc. %</th>
                <th style={{ ...s.th, width: '7%',  textAlign: 'right' }}>Disc. Price ₹</th>
                <th style={{ ...s.th, width: '5%',  textAlign: 'center' }}>Qty</th>
                <th style={{ ...s.th, width: '5%',  textAlign: 'center' }}>GST %</th>
                <th style={{ ...s.th, width: '7%',  textAlign: 'right' }}>CGST ₹</th>
                <th style={{ ...s.th, width: '7%',  textAlign: 'right' }}>SGST ₹</th>
                <th style={{ ...s.th, width: '8%',  textAlign: 'right' }}>Subtotal ₹</th>
                <th style={{ ...s.th, width: '3%'  }}></th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item, idx) => (
                <tr key={idx}>
                  <td style={{ ...s.td, textAlign: 'center', color: '#94a3b8', fontSize: '0.8rem' }}>{idx + 1}</td>

                  <td style={s.td}>
                    <Select
                      options={batchOptions}
                      value={item.batch_id ? batchOptions.find((o) => o.value === item.batch_id) ?? null : null}
                      onChange={(opt) => { if (opt) selectBatch(idx, opt.batch); }}
                      placeholder="Search item or batch…"
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                  </td>

                  <td style={s.td}>
                    <input type="text" style={s.cellInput} value={item.hsn_sac}
                      onChange={(e) => updateItem(idx, 'hsn_sac', e.target.value)} placeholder="HSN" />
                  </td>

                  {/* Dealer Price — read-only internal reference */}
                  <td style={s.td}>
                    <input
                      type="number"
                      style={{ ...s.cellInput, textAlign: 'right', backgroundColor: '#f8fafc', color: '#64748b' }}
                      value={item.dealer_price}
                      readOnly
                      disabled
                      placeholder="—"
                    />
                  </td>

                  <td style={s.td}>
                    <input type="number" style={{ ...s.cellInput, textAlign: 'right' }} value={item.mrp}
                      onChange={(e) => updateItem(idx, 'mrp', e.target.value === '' ? '' : Number(e.target.value))}
                      placeholder="0.00" min={0} />
                  </td>

                  <td style={s.td}>
                    <input type="number" style={{ ...s.cellInput, textAlign: 'right' }} value={item.discount_rate ? Math.trunc(item.discount_rate) : ''}
                      onChange={(e) => updateItem(idx, 'discount_rate', Number(e.target.value) || 0)}
                      placeholder="0" min={0} max={100} />
                  </td>

                  <td style={s.td}>
                    <input type="number" style={{ ...s.cellInput, textAlign: 'right' }} value={item.disc_price || ''}
                      onChange={(e) => updateItem(idx, 'disc_price', Number(e.target.value) || 0)}
                      placeholder="0.00" min={0} />
                  </td>

                  <td style={s.td}>
                    <input type="number" style={{ ...s.cellInput, textAlign: 'center' }} value={item.quantity}
                      onChange={(e) => updateItem(idx, 'quantity', e.target.value)} placeholder="0" min={1} />
                  </td>

                  <td style={s.td}>
                    <input type="number" style={{ ...s.cellInput, textAlign: 'center' }} value={item.gst_percent || ''}
                      onChange={(e) => updateItem(idx, 'gst_percent', Number(e.target.value) || 0)}
                      placeholder="0" min={0} />
                  </td>

                  <td style={s.td}>
                    <input
                      type="number"
                      style={{ ...s.cellInput, textAlign: 'right' }}
                      value={item.cgst_amount || ''}
                      onChange={(e) => updateItem(idx, 'cgst_amount', Number(e.target.value) || 0)}
                      placeholder="0.00"
                      min={0}
                    />
                  </td>
                  <td style={s.td}>
                    <input
                      type="number"
                      style={{ ...s.cellInput, textAlign: 'right' }}
                      value={item.sgst_amount || ''}
                      onChange={(e) => updateItem(idx, 'sgst_amount', Number(e.target.value) || 0)}
                      placeholder="0.00"
                      min={0}
                    />
                  </td>
                  <td style={{ ...s.td, textAlign: 'right', fontWeight: 700, fontSize: '0.85rem', color: '#0f172a' }}>
                    {item.subtotal > 0 ? `₹${fmtAmt(item.subtotal)}` : '—'}
                  </td>
                  <td style={{ ...s.td, textAlign: 'center' }}>
                    <button style={s.deleteBtn} onClick={() => removeItem(idx)}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <button
          onClick={() => setLineItems((p) => [...p, emptyItem()])}
          style={{
            padding: '0.3rem 0.8rem', borderRadius: '5px', cursor: 'pointer',
            fontSize: '0.78rem', fontWeight: 500, border: '1px dashed #7dd3fc',
            backgroundColor: '#f0f9ff', color: '#0369a1', marginBottom: '1rem',
          }}
        >
          + Add Row
        </button>

        {grandTotal > 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div style={s.summaryBox}>
              <div style={s.summaryLine}><span>Subtotal (ex-GST)</span><span>₹{fmtAmt(totalExGst)}</span></div>
              <div style={s.summaryLine}><span>Total CGST</span><span>₹{fmtAmt(totalCgst)}</span></div>
              <div style={s.summaryLine}><span>Total SGST</span><span>₹{fmtAmt(totalSgst)}</span></div>
              {roundOff > 0 && (
                <div style={s.summaryLine}><span>Rounded Off</span><span>− ₹{fmtAmt(roundOff)}</span></div>
              )}
              <div style={s.grandTotalLine}><span>Net Total Payable</span><span>₹{fmtAmt(grandTotal)}</span></div>
            </div>
          </div>
        )}
      </div>

      {/* Remarks */}
      <div style={s.card}>
        <p style={s.sectionTitle}>Remarks</p>
        <textarea
          style={s.textarea}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Any additional notes for this consumable invoice…"
        />
      </div>

      {/* Bottom action row */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.5rem' }}>
        <button style={btn('outline')} onClick={() => setShowPreview(true)}>Preview Invoice</button>
        {status !== 'Deleted' && (
          <button style={btn('primary')} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        )}
      </div>

      {/* Preview modal */}
      {showPreview && patient && (
        <div
          style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 3000, overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px' }}
          onClick={() => setShowPreview(false)}
        >
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }} onClick={(e) => e.stopPropagation()}>
            <button
              style={{ padding: '0.6rem 1.5rem', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}
              onClick={handleDownloadPDF}
              disabled={downloadingPDF}
            >
              {downloadingPDF ? 'Generating PDF...' : '⬇ Download PDF'}
            </button>
            <button
              style={{ padding: '0.6rem 1.5rem', backgroundColor: 'white', color: '#334155', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}
              onClick={() => setShowPreview(false)}
            >
              ✕ Close
            </button>
          </div>
          <div onClick={(e) => e.stopPropagation()}>
            <ConsumableInvoiceDocument
              ref={invoiceRef}
              patient={patient}
              billNo={typeof billNo === 'number' ? billNo : 0}
              invoiceNo={invoiceNo}
              billDate={billDate}
              periodStart={periodStart}
              periodEnd={periodEnd}
              lineItems={[]}
              consumableItems={lineItems.map((item) => ({
                particulars: item.batch_display || 'Consumable',
                hsn_sac: item.hsn_sac,
                mrp: item.mrp !== '' ? item.mrp : null,
                discount_rate: item.discount_rate,
                disc_price: item.disc_price,
                quantity: Number(item.quantity) || 0,
                gst_percent: item.gst_percent,
                cgst_amount: item.cgst_amount,
                sgst_amount: item.sgst_amount,
                subtotal: item.subtotal,
              }))}
              remarks={remarks}
              billType="consumables"
              createdAt={new Date().toISOString()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default EditConsumableBill;
